import * as React            from 'react';
import {cx, css}             from 'emotion';
import {cs}                  from '@Common/react/chain-services';

import {FontAwesomeIcon}   from "@fortawesome/react-fontawesome";
import {faCheck,
         faSpinner}        from "@fortawesome/free-solid-svg-icons";

import {ReactGAGuestEvent}            from "@Common/libs/ga/xd-react-ga";
import {Load}                         from "@Common/react/load";
import {SearchableSelect}             from "@Common/ui-components/searchable-select/searchable-select";
import {sortMulti}                    from "@Common/utils/collections";
import {chain}                        from "@Common/utils/fs";
import {values}                       from "@Common/utils/objects";
import {scopedState}                  from '@Common/react/scoped-state';
import {UseState}                     from '@Common/react/use-state';
import {scrollIntoView}               from '@Common/ui-components/light/scroll-into-view';
import {ReCaptcha}                    from '@Common/ui-components/recaptcha/recaptcha';
import {removeURLParameterFromWindow} from '@Common/utils/remove-url-parameter';

import {OfficeCreateModal} from "@Xpresso/routes/account/admin/office-management/office-management-route/office-create-modal/office-create-modal";

import {consumeContext}         from '../../../../../../common/react/context';
import {ConfirmDialog}          from '../../../../../../common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog';
import {PasswordInput}          from '../../../../../../common/ui-components/password-input/password-input';
import {FacebookLoginButton}    from '../facebook-login-button';
import {GoogleLoginButton}      from '../google-login-button';
import "./create-account-modal.scss";

export const CreateAccountModal = ({apiError, values, content, domRef}) => cs(
    consumeContext("apis"),
    consumeContext("session"),
    consumeContext("systemConstants"),
    consumeContext("themeScheme"),
    ["enableGuestCheckout", ({themeScheme}, next) => next(themeScheme?.enableGuestCheckout)],
    ["idCompanyBranded", ({themeScheme}, next) => next(themeScheme?.companyId)],
    ["companyXdocsUrl", ({}, next) => next(window.location.pathname.split("/")[2] ?? 'xd')],
    ["defaultOffices", ({apis}, next) => Load({
        fetch: () => apis.next.getDefaultOffices(),
        next
    })],
    ["idBranch", ({defaultOffices}, next) => UseState({defaultValue: defaultOffices?.[0]?.idBranch, next})],
    ["confirmCloseDialog", ({}, next) => ConfirmDialog({next})],
    ["captchaTokenState", ({}, next) => UseState({initValue: null, next})],
    ["validation", ({}, next) => next({
        length: values.value?.password?.length >= 6,
        number: /\d/.test(values.value?.password),
        lowercase: values.value?.password && /[a-z]/.test(values.value?.password),
        uppercase: /[A-Z]/.test(values.value?.password),
        special: /[@$!%*#?&]/.test(values.value?.password),
        matches: values.value?.password && values.value?.password === values.value?.confirmPassword,
        full: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/.test(values.value?.password) && values.value?.password === values.value?.confirmPassword
    })],
    ["validationLines", ({validation}, next) => next(getValidationLines({validation}))],
    ["submit", ({}, next) => UseState({initValue: false, next})],
    ["submitting", ({}, next) => UseState({initValue: false, next})],
    ["showCreateOffice", ({}, next) => UseState({initValue: false, next})],
    ({apis, captchaTokenState, enableGuestCheckout, defaultOffices, idBranch, idCompanyBranded,
      validation, validationLines, showCreateOffice, submit, submitting, themeScheme}) => (
        <div className="create-account-modal-c4m3 create-account-modal">
            <div className="content-container">
                <div className="header" ref={domRef.set}>
                    <div className="title" xd-qa-type="label" xd-qa-name="Create Account">
                        Create Account
                    </div>
                    <div className="sub-title">
                        New to Xpressdocs? Create an account below
                    </div>
                </div>
                <hr className="divider" />
                {fields.email.showDuplicateEmailError && <div className="error-message duplicate-email">
                    We have identified an existing account with this email address. Click “Sign in” below to sign in to your existing Xpressdocs account.
                </div>}
                {apiError.value?.message && <div className="error-message" xd-qa-type="text" xd-qa-name="Error Message">
                    {apiError.value.message}
                </div>}
                <div className="info-section">
                    <div className={cx("user-email section", {error: fields.email.showError})}>
                        <div className="email-label label">
                            {enableGuestCheckout ? fields.email.label : "Email / Username"}
                        </div>
                        <div className="email-input input">
                            <input {...{
                                value: scopedState(values, ["email"]).value,
                                onChange: (e) => {
                                    fields.email.showError = false;
                                    let value = e.target.value;
                                    scopedState(values, ["email"]).onChange(value);
                                    apiError.onChange("")
                                },
                                className: "value",
                                maxLength: 64,
                                "xd-qa-type": "textbox",
                                "xd-qa-name": "email",
                            }}/>
                        </div>
                        {submit.value && fields.email.showError && fields.email.errorMessage(scopedState(values, ["email"]).value)}
                    </div>
                    <div className={cx("user-name section", {error: fields.firstName.showError || fields.lastName.showError})}>
                        <div className={cx("first-name-section", {error: fields.firstName.showError})}>
                            <div className="first-name-label label">
                                {fields.firstName.label}
                            </div>
                            <div className="first-name-input input">
                                <input {...{
                                    value: scopedState(values, ["firstName"]).value,
                                    onChange: (e) => {
                                        fields.firstName.showError = false;
                                        let value = e.target.value;
                                        scopedState(values, ["firstName"]).onChange(value);
                                    },
                                    autoComplete: "given-name",
                                    className: "value",
                                    maxLength: 64,
                                    "xd-qa-type": "textbox",
                                    "xd-qa-name": "first-name",
                                }}/>
                            </div>
                            {submit.value && fields.firstName.showError && fields.firstName.errorMessage(scopedState(values, ["firstName"]).value)}
                        </div>
                        <div className={cx("last-name-section", {error: fields.lastName.showError})}>
                            <div className="last-name-label label">
                                {fields.lastName.label}
                            </div>
                            <div className="last-name-input input">
                                <input {...{
                                    value: scopedState(values, ["lastName"]).value,
                                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                        fields.lastName.showError = false;
                                        let value = e.target.value;
                                        scopedState(values, ["lastName"]).onChange(value);
                                    },
                                    autoComplete: "family-name",
                                    className: "value",
                                    maxLength: 64,
                                    "xd-qa-type": "textbox",
                                    "xd-qa-name": "last-name",
                                }}/>
                            </div>
                            {submit.value && fields.lastName.showError && fields.lastName.errorMessage(scopedState(values, ["lastName"]).value)}
                        </div>
                    </div>
                    <div className={cx("user-password section", {error: fields.password.showError})}>
                        <div className="password-label label">
                            {fields.password.label}
                        </div>
                        <div className="password-input input" xd-qa-type="card" xd-qa-name="Password">
                            {PasswordInput({inputProps:{
                                value: scopedState(values, ["password"]).value,
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    fields.password.showError = false;
                                    let value = e.target.value;
                                    scopedState(values, ["password"]).onChange(value);
                                },
                                autoComplete: "new-password",
                                className: "value",
                                maxLength: 64,
                                "xd-qa-name": "Password",
                            }})}
                            {submit.value && fields.password.showError && fields.password.errorMessage(validation)}
                        </div>
                        <div className="validation-section">
                            {validationLines.map((line, i) => (
                                <div className="validation-line" key={i} xd-qa-type="label" xd-qa-name={line.message}>
                                    {line.validated ? (
                                        <div className="validation-icon" xd-qa-type="icon" xd-qa-name="Check"><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                                    ) : (
                                        <div className="validation-spacer"/>
                                    )}
                                    <div className="validation-label">{line.message}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={cx("user-confirm-password section", {error: fields.confirmPassword.showError})}>
                        <div className="confirm-password-label label">
                            {fields.confirmPassword.label}
                        </div>
                        <div className="confirm-password-input input" xd-qa-type="card" xd-qa-name="Confirm Password">
                            {PasswordInput({inputProps:{
                                value: scopedState(values, ["confirmPassword"]).value,
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    fields.confirmPassword.showError = false;
                                    let value = e.target.value;
                                    scopedState(values, ["confirmPassword"]).onChange(value);
                                },
                                autoComplete: "new-password",
                                className: "value",
                                maxLength: 64,
                                "xd-qa-name": "Confirm Password",
                            }})}
                            {submit.value && fields.confirmPassword.showError && fields.confirmPassword.errorMessage(validation)}
                        </div>
                        <div className="validation-section">
                            <div className="validation-line" xd-qa-type="label" xd-qa-name={"Passwords match"}>
                            {validation.matches ? (
                                <div className="validation-icon" xd-qa-type="icon" xd-qa-name="Check"><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                            ) : (
                                <div className="validation-spacer"/>
                            )}
                            <div className="validation-label">Passwords match</div>
                            </div>
                        </div>
                    </div>
                </div>
                {
                    (!enableGuestCheckout && defaultOffices?.length !== 1) &&
                    <div className="office-selection">
                        <div className="confirm-password-label label">
                                Select Office
                        </div>
                        {officeSelect({defaultOffices, idBranch})}
                        {themeScheme?.allowBranchCreate &&
                            <React.Fragment>
                                <a className="show-create-office"
                                    onClick={() => showCreateOffice.onChange(true)}
                                >
                                    Create New Office
                                </a>
                                {OfficeCreateModal({
                                    show: showCreateOffice.value,
                                    reload: () => null,
                                    idCompanyBranded: idCompanyBranded,
                                })}
                            </React.Fragment>
                        }
                    </div>
                }
                <div className="recaptcha" xd-qa-type="card" xd-qa-name="Recaptcha">
                        <ReCaptcha onExpiration={() => captchaTokenState.onChange(null)} onVerification={(value) => {
                            fields.captcha.showError = false;
                            captchaTokenState.onChange(value)
                        }}/>
                        {submit.value && fields.captcha.showError && fields.captcha.errorMessage()}
                    </div>
                <div className="footer">
                    <button
                        xd-qa-type="button"
                        xd-qa-name="Create Account"
                        className="create"
                        disabled={submitting.value}
                        onClick={async () => {
                            apiError.onChange(null);
                            submitting.onChange(true)
                            removeURLParameterFromWindow({parameter: "modal"})
                            if(isValid({fields, values, validation, captchaTokenState})) {
                                const createFields = {
                                    agentEmail: values.value.email,
                                    agentNameFirst: values.value.firstName,
                                    agentNameLast: values.value.lastName,
                                    userPassword: values.value.password,
                                }

                                if(enableGuestCheckout) {
                                    createFields['captcha'] = captchaTokenState.value
                                }

                                if(!enableGuestCheckout) {
                                    createFields['idBranch'] = idBranch.value
                                    createFields['userName'] = values.value.email
                                }

                                try {
                                    const data = enableGuestCheckout
                                    ? await apis.next.session.registerGuestAgent({createFields})
                                    : await apis.next.authenticate.agentNewSignup({createFields})

                                    if(data.statusCode !== "success") {
                                        fields.email.showError = false;
                                        ReactGAGuestEvent({
                                            eventName: "create_account_failed",
                                            method: "username",
                                            reason: data.message,
                                            statusCode: data.statusCode
                                        }),
                                        apiError.onChange(data)
                                        scrollIntoView(domRef.get())
                                        grecaptcha.reset()
                                    } else {
                                        ReactGAGuestEvent({
                                            eventName: "create_account",
                                            method: "username",
                                            reason: data.message,
                                            statusCode: data.statusCode
                                        }),
                                        location.reload()
                                    }
                                } catch(e) {
                                    console.log(e)
                                }
                            } else {
                                submit.onChange(true)
                                grecaptcha.reset()
                            }
                            submitting.onChange(false)
                        }}
                    >
                        {submitting.value ? <FontAwesomeIcon icon={faSpinner} className="fa-pulse"/> : "Create Account"}
                    </button>
                    {enableGuestCheckout &&
                        <>
                            {(themeScheme?.enableGoogleIdLogin || themeScheme?.enableMetaIdLogin) &&
                                <div className="separator">
                                    <hr /> <span className="login-text">Or login using</span><hr />
                                </div>
                            }
                            {(themeScheme?.enableGoogleIdLogin) && <div className="social-buttons">
                                {GoogleLoginButton({content, apiError, domRef})}
                            </div>}
                            {(themeScheme?.enableMetaIdLogin) && <div className="social-buttons">
                                {FacebookLoginButton({content, apiError, domRef})}
                            </div>}
                            <div className="label">
                                Have an Xpressdocs account already?<span className="sign-in"><a
                                    onClick={() => {
                                        scopedState(values, ["password"]).onChange("");
                                        scopedState(values, ["confirmPassword"]).onChange("");
                                        scopedState(content, ["content"]).onChange("signin");
                                    }}
                                    xd-qa-type="link"
                                    xd-qa-name="Sign In"
                                > Sign In</a></span>
                            </div>
                        </>
                    }
                </div>
            </div>
        </div>
    )
)

const getValidationLines = ({validation}) => ([
    {
        "message": "At least 6 characters",
        "validated": validation.length,
    },
    {
        "message": "At least 1 number",
        "validated": validation.number,
    },
    {
        "message": "At least 1 lowercase letter",
        "validated": validation.lowercase,
    },
    {
        "message": "At least 1 uppercase letter",
        "validated": validation.uppercase,
    },
    {
        "message": "At least 1 special character @$!%*#?&",
        "validated": validation.special,
    },
]);

const fields = {
    email: {
        label: "Email",
        valid: (email) => { return emailRegex.test(email) },
        errorMessage: (email) => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Email Error Message">
                {email === "" ? (
                    "Email required"
                ) : !emailRegex.test(email) ? (
                    "Invalid email"
                ) : ""}
            </div>
        ),
        showError: false,
        showDuplicateEmailError: false,
    },
    firstName: {
        label: "First Name",
        valid: (name) => { return name !== "" && name.length >= 2 },
        errorMessage: (name) => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="First Name Error Message">
                {name === "" ? (
                    "First name required"
                ) : name.length < 2 ? "At least 2 characters" : ""}
            </div>
        ),
        showError: false,
    },
    lastName: {
        label: "Last Name",
        valid: (name) => { return name !== "" && name.length >= 2 },
        errorMessage: (name) => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Last Name Error Message">
                {name === "" ? (
                    "Last name required"
                ) : name.length < 2 ? "At least 2 characters" : ""}
            </div>
        ),
        showError: false,
    },
    password: {
        label: "Password",
        valid: (validation) => { return values(validation).every(Boolean) },
        errorMessage: (validation) => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Password Error Message">
                {!validation.matches ? (
                    "Passwords must match"
                ) : values(validation).every(Boolean) ? (
                    ""
                )  : "Invalid Password"}
            </div>
        ),
        showError: false,
    },
    confirmPassword: {
        label: "Confirm Password",
        valid: (validation) => { return validation.matches },
        errorMessage: (validation) => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Confirm Password Error Message">
                {!validation.matches ? (
                    "Passwords must match"
                ) : ""}
            </div>
        ),
        showError: false,
    },
    captcha: {
        valid: (captchaTokenState) => {return captchaTokenState.value},
        errorMessage: () => (
            <div className="error-message" xd-qa-type="text" xd-qa-name="Recaptcha Error Message">
                Captcha Required
            </div>
        ),
        showError: false,
    }
}

const officeSelect = ({defaultOffices, idBranch}) => {

    return (
        <div className="office-select">
            {SearchableSelect({
                getSublabel: (branch) => (<>
                    <div>
                        {[
                            branch.branchAddress1,
                            branch.branchAddress2,
                            branch.branchCity,
                            branch.branchState,
                            branch.branchZip,
                        ].filter(Boolean).join(", ")}
                    </div>
                    <div
                        xd-qa-type="dropdown_item"
                        xd-qa-name={`Office ${branch.idBranch}`}
                    >
                        {branch.branchLinkId}
                    </div>
                </>),
                list: chain(
                    defaultOffices ?? [],
                    (_) => sortMulti(_, [
                        (branch) => branch["branchName"]?.toLowerCase(),
                    ]),
                ),
                loading: !defaultOffices,
                placeholder: "Select an office",
                getLabel: (branch) => chain(
                    branch.branchName || branch.branchLinkId,
                    (label) => <span title={label} xd-qa-type="dropdown_item" xd-qa-name={`Office ${label}`}>{label}</span>
                ),
                mapSearchAsItemFilter: (search:string) => {
                    const searchLowercase = search.toLowerCase();
                    return (branch) => [
                        "branchName",
                        "branchLinkId",
                        "branchAddress1",
                        "branchAddress2",
                        "branchCity",
                        "branchState",
                    ].some(field => branch[field].toLowerCase().includes(searchLowercase))
                },
                onSelect: (branch) => idBranch.onChange(branch.idBranch),
                isSelected: (branch) => idBranch.value === branch.idBranch,

                className: "office",
                searchLabel: "Search an Office",
                xdQaName: "Modal Office Dropdown",
                scrollCloses: false,
            })}
        </div>
    )
}

function isValid({fields, values, validation, captchaTokenState}) {
    fields.email.showError = !fields.email.valid(values.value.email)
    fields.firstName.showError = !fields.firstName.valid(values.value.firstName)
    fields.lastName.showError = !fields.lastName.valid(values.value.lastName)
    fields.password.showError = !fields.password.valid(validation)
    fields.confirmPassword.showError = !fields.confirmPassword.valid(validation)
    fields.captcha.showError  = !fields.captcha.valid(captchaTokenState)

    return fields.email.valid(values.value.email) && fields.firstName.valid(values.value.firstName) && fields.lastName.valid(values.value.lastName) &&
                fields.password.valid(validation) && fields.confirmPassword.valid(validation) && captchaTokenState.value
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
